import React, { useEffect, useState } from 'react';
import { SYNC_AUTH_EVENT, getEffectiveApiKey, isApiKeyFromEnv } from '../../services/syncApi';

const hasKey = () => Boolean(getEffectiveApiKey());

const SyncAuthBanner = () => {
  const [state, setState] = useState(() => ({
    missing: !hasKey(),
    lastError: null, // {status, detail, hasKey}
  }));

  useEffect(() => {
    const onAuthError = (e) => {
      setState((prev) => ({ ...prev, lastError: e.detail || { status: 401, detail: 'auth failed' } }));
    };
    const onStorage = (e) => {
      if (e.key === 'syncApiKey') {
        setState((prev) => ({ missing: !hasKey(), lastError: hasKey() ? null : prev.lastError }));
      }
    };
    window.addEventListener(SYNC_AUTH_EVENT, onAuthError);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYNC_AUTH_EVENT, onAuthError);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Nothing to say.
  if (!state.missing && !state.lastError) return null;

  // 503 path: server has SYNC_API_KEYS unset; pasting any key won't help.
  if (state.lastError?.status === 503) {
    return (
      <div className="sync-auth-banner error" role="alert">
        <strong>Backend auth not configured: </strong>
        the server returned 503 because <code>SYNC_API_KEYS</code> is empty.
        Set it in <code>.env</code> and restart the backend, then refresh.
      </div>
    );
  }

  // 401 with a key set: key is wrong.
  if (state.lastError?.status === 401 && state.lastError?.hasKey) {
    const fromEnv = state.lastError?.keyFromEnv || isApiKeyFromEnv();
    return (
      <div className="sync-auth-banner error" role="alert">
        <strong>Invalid API key. </strong>
        {fromEnv ? (
          <>
            The key currently in use came from <code>frontend/.env</code> (
            <code>REACT_APP_SYNC_API_KEY</code>). It doesn't match any value in
            the backend's <code>SYNC_API_KEYS</code>. Update one of the two so
            they line up, then restart the React dev server (CRA only reads
            <code>.env</code> at startup) or paste a fresh key via the API key
            control in the top bar.
          </>
        ) : (
          <>
            Update the <strong>API Key</strong> field via the key control in the
            top bar to match one of the values in <code>SYNC_API_KEYS</code> on
            the backend, then click <strong>Save</strong>.
          </>
        )}
      </div>
    );
  }

  // Missing key (covers both first-load and post-401-without-key).
  return (
    <div className="sync-auth-banner warning" role="alert">
      <strong>API key required. </strong>
      Write operations (enqueue, override, mapping edits) need an
      <code> X-Sync-API-Key</code> header. Set it via the <strong>API key</strong>
      control in the top bar and click <strong>Save</strong>.
    </div>
  );
};

export default SyncAuthBanner;
